<template>
  <mia-form @submit="save">
    <mia-form-section
      title="SEPA Lastschrift"
      description="Das Lastschriftverfahren ermöglicht Ihnen eine komfortable Zahlungsabwicklung."
    >
      <mia-form-section-grid>
        <!-- Accounting Enabled -->
        <div class="col-span-2">
          <mia-feature-toggle
            v-model="isSepaEnabled"
            label="Lastschrift aktiv"
            label-position="top"
            :labels="{
              unchecked: 'Nein',
              checked: 'Ja',
            }"
            :disabled="!isRootOrAdmin"
            @input="onSepaEnabledInput"
          />

          <div class="relative">
            <mia-input-error :errors="getErrors('item_accounting_sepa')" />
          </div>
        </div>

        <div
          v-if="!isSepaEnabled"
          class="col-span-2"
        >
          <mia-notice permanent>
            <p class="my-0">
              Aktivieren Sie die Lastschrift für eine komfortable Zahlungsabwicklung
            </p>
          </mia-notice>
        </div>
        <template v-else>
          <!-- Accounting Holder -->
          <div>
            <mia-feature-input
              v-model="form.item_accounting_holder"
              label="Kontoinhaber"
              required
              :errors="getErrors('item_accounting_holder')"
              @input="clearErrors('item_accounting_holder')"
            />
          </div>

          <!-- Accounting IBAN -->
          <div>
            <mia-feature-input
              v-model="form.item_accounting_iban"
              label="IBAN"
              required
              :errors="getErrors('item_accounting_iban')"
              @input="clearErrors('item_accounting_iban')"
            />
          </div>

          <!-- Accounting BIC -->
          <div>
            <mia-feature-input
              v-model="form.item_accounting_bic"
              label="BIC"
              required
              :errors="getErrors('item_accounting_bic')"
              @input="clearErrors('item_accounting_bic')"
            />
          </div>
        </template>
      </mia-form-section-grid>
    </mia-form-section>

    <template #actions>
      <div class="flex items-center justify-between w-full">
        <mia-section-progress
          group="settingsBasics"
          page="invoice"
          section="sepaData"
        />

        <mia-submit-button
          :disabled="$wait.any"
          :is-loading="$wait.is('updating invoice sepa data')"
        />
      </div>
    </template>
  </mia-form>
</template>

<script>
import { mapGetters } from 'vuex'
import { updateInvoiceSepaData } from '@/api/settings'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import SectionProgress from '@/components/cars/edit/SectionProgress.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import { handleYNBool } from '@/helpers/utils'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'

export default {
  name: 'MiaSettingsBasicsInvoiceSepaForm',

  components: {
    'mia-feature-input': FeatureInput,
    'mia-feature-toggle': FeatureToggle,
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-form-section-grid': FormSectionGrid,
    'mia-notice': Notice,
    'mia-section-progress': SectionProgress,
    'mia-submit-button': SubmitButton
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      form: {
        item_accounting_sepa: '',
        item_accounting_holder: '',
        item_accounting_iban: '',
        item_accounting_bic: ''
      },
      isSepaEnabled: false
    }
  },

  computed: {
    ...mapGetters('settings', ['settings'])
  },

  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(settings) {
        this.$set(this.form, 'item_accounting_sepa', settings.item_accounting_sepa)
        this.$set(this.form, 'item_accounting_holder', settings.item_accounting_holder)
        this.$set(this.form, 'item_accounting_iban', settings.item_accounting_iban)
        this.$set(this.form, 'item_accounting_bic', settings.item_accounting_bic)

        this.isSepaEnabled = handleYNBool(settings.item_accounting_sepa)
      }
    }
  },

  methods: {
    async save() {
      this.resetErrors()
      this.$wait.start('updating invoice sepa data')

      const payload = {
        item_accounting_holder: this.form.item_accounting_holder,
        item_accounting_iban: this.form.item_accounting_iban,
        item_accounting_bic: this.form.item_accounting_bic
      }

      if (this.isRootOrAdmin) {
        payload.item_accounting_sepa = this.form.item_accounting_sepa
      }

      const [error] = await updateInvoiceSepaData(payload)

      this.$wait.end('updating invoice sepa data')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })

        this.handleBackendValidationErrors(error)
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich gespeichert.']
      })

      await this.$store.dispatch('settings/loadSettings')
    },

    onSepaEnabledInput(isEnabled) {
      this.form.item_accounting_sepa = handleYNBool(isEnabled)
    }
  }
}
</script>
